import React, { Component } from 'react'
import { render } from 'react-dom'

import '../../App.css'

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dark, darcula } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { InlineMath, BlockMath } from 'react-katex';

import {
  Header,
} from 'semantic-ui-react'

import 'katex/dist/katex.min.css';

class Chapter201 extends Component {
  render() {
    return (
      <div className="base">

        <Header as='h1'>
        2-1. ガンマ・ポアソン・リーセンシー・モデル
        </Header>

「最近いつ買ったか」、「最近いつ訪れたか」というデータから$m$と$k$を計算し、NBDモデルを構築する事が出来ます。NBDモデルを記述する式は、何度も紹介したとおり、以下の様に計算できます。 <br/>

<BlockMath>{`
P\\left(r \\right) = \\frac{\\left(1 + \\frac{M}{K} \\right)^{-K} \\cdot \\Gamma\\left(K + r \\right)}{\\Gamma\\left(r + 1 \\right)\\cdot \\Gamma\\left(K \\right)} \\cdot \\left(\\frac{M}{M+K} \\right)^r 
`}</BlockMath>

ある製品の期間<InlineMath>{`\\displaystyle t `}</InlineMath>の対応する平均値<InlineMath>{`\\displaystyle M `}</InlineMath>を<InlineMath>{`\\displaystyle mt `}</InlineMath>とし、<InlineMath>{`\\displaystyle K `}</InlineMath>を<InlineMath>{`\\displaystyle k `}</InlineMath>と表記します。浸透率は100%から一度もこの製品を購入しない人を引けば良いので、 <br/>


<BlockMath>{`
\\begin{aligned}
P(t) &=1-P\_0\\left(r=0 \\right) \\\\\\\\
&= 1 - \\frac{\\left(1 + \\frac{mt}{k} \\right)^{-k} \\cdot \\Gamma\\left(k + 0 \\right)}{\\Gamma\\left(0 + 1 \\right)\\cdot \\Gamma\\left(k \\right)} \\cdot \\left(\\frac{mt}{mt+k} \\right)^0  \\\\\\\\
&= 1 - \\left(1 + \\frac{mt}{k} \\right)^{-k}
\\end{aligned}
`}</BlockMath>

となります。よってある期間<InlineMath>{`\\displaystyle t `}</InlineMath>と<InlineMath>{`\\displaystyle t - 1 `}</InlineMath>における浸透率は、

<BlockMath>{`

\\begin{aligned}
 P\\left(t \\right) - P\\left(t-1 \\right) &= 
\\left(1+\\frac{m\\times t}{k} \\right)^{-k} - \\left(1+\\frac{m\\times \\left(t-1 \\right)}{k} \\right)^{-k}
\\end{aligned}

`}</BlockMath>

となります。 <br/>

これを任意の期間に適用するために、二つの変数<InlineMath>{`\\displaystyle t_1 `}</InlineMath>と<InlineMath>{`\\displaystyle t_2 `}</InlineMath>を用いて、以下の様に<InlineMath>{`f \\left(x \\right)`}</InlineMath>と定義します。

<BlockMath>{`

f\\left(t1,t2,m,k \\right) =  \\left(1+\\frac{m\\times t_1}{k} \\right)^{-k} - \\left(1+\\frac{m\\times t_2}{k} \\right)^{-k}

`}</BlockMath>

教科書の表10-1は共通の関数<InlineMath>{`f\\left(x \\right)`}</InlineMath>を用いて、以下の様に表現することが出来ます。


<table border="1" className="table_base">
  <tr>
    <th className="th_01">ガンマ分布</th>
    <th className="th_01">実測値</th>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=0,t_2= \\frac{14}{31}\\right) `}</InlineMath> </td>
    <td className="td_01">43.9%</td>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=\\frac{14}{31},t_2=1 \\right) `}</InlineMath> </td>
    <td className="td_01">25.6%</td>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=1,t_2=2 \\right) `}</InlineMath> </td>
    <td className="td_01">19.1%</td>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=2,t_2=3 \\right) `}</InlineMath> </td>
    <td className="td_01">5.1%</td>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=3,t_2=4 \\right) `}</InlineMath> </td>
    <td className="td_01">1.5%</td>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=4,t_2=5 \\right) `}</InlineMath> </td>
    <td className="td_01">0.7%</td>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=5,t_2=6 \\right) `}</InlineMath> </td>
    <td className="td_01">1.4%</td>
  </tr>
  <tr>
    <td className="td_01"><InlineMath>{`\\displaystyle  f\\left(t_1=6,t_2=\\infty \\right) `}</InlineMath> </td>
    <td className="td_01">2.7%</td>
  </tr>
</table>


        <Header as='h2'>
        scipyのcurve_figによる<InlineMath>{` \\displaystyle m,k `}</InlineMath>の導出
        </Header>

一般に非線形の関数に対して最小二乗法によるfittingを行うには、<a href="https://docs.scipy.org/doc/scipy/reference/generated/scipy.optimize.curve_fit.html" target="_blank">scipy</a>のcurve_fitモジュールを利用します。scipyのウェブサイトによると、


        <SyntaxHighlighter language="python" style={darcula}>
{`
  scipy.optimize.curve_fit(f, xdata, ydata, p0=None, sigma=None, absolute_sigma=False, check_finite=True, bounds=(-inf, inf), method=None, jac=None, **kwargs)[source]
`}
        </SyntaxHighlighter>


となっており、さらに、<InlineMath>{`\\displaystyle xdata `}</InlineMath>と<InlineMath>{`\\displaystyle ydata `}</InlineMath>は

        <SyntaxHighlighter language="python" style={darcula}>
{`
  xdata : array_like
  The independent variable where the data is measured. Must be an M-length sequence or an (k,M)-shaped array for functions with k predictors.
  
  ydata : array_like
  The dependent data, a length M array - nominally f(xdata, ...).
`}
        </SyntaxHighlighter>

と定義されています。教科書のfittingの問題を解くためには、


<BlockMath>{`
 f\\left(t1,t2,m,k \\right) =\\left(1+\\frac{m\\times t1}{k} \\right)^{-k} -  \\left(1+\\frac{m\\times t2}{k} \\right)^{-k} 
`}</BlockMath>

という上記で定義した関数<InlineMath>{` \\displaystyle f\\left(x \\right)`}</InlineMath>に対して、２変数関数のfitting問題を解く事になります。２変数は期間を指定する２変数（２週間〜１ヶ月の購入数を求める際は、 <InlineMath>{`\\displaystyle t_1=\\frac{14}{31}, t_2=1`}</InlineMath>となります）が必要で、以下のように二次元配列で定義します。

        <SyntaxHighlighter language="python" style={darcula}>
{`
  x = np.array([
    [0.0 ,14/31 ,1.0 ,2.0 ,3.0 ,4.0 ,5.0 ,6.0 ],
    [14/31 ,1.0  ,2.0 ,3.0 ,4.0 ,5.0 ,6.0 , 10000.0]
  ])

`}
        </SyntaxHighlighter>

x[0]が<InlineMath>{`\\displaystyle t_1 `}</InlineMath>の配列、x[1]がt_2の配列となります。x[1,7]=10000.0としているのは、本来は<InlineMath>{`\\displaystyle \\infty `}</InlineMath>となりますが、実際の数値計算では無限大は受けいられないので、事実上無限大となる10000を入れています。この値は100程度でも問題ありません。

fittingする実際のコードは以下の通りです。

        <SyntaxHighlighter language="python" style={darcula}>

{`
import json
import numpy as np

from scipy.optimize import curve_fit 
from scipy.special import gamma

def _get_delta_nbd(x, m, k):
  return (1 + m * x[0] / k )**(-k) - (1 + m * x[1] / k )**(-k) 

x = np.array([
  [0.0   ,14/31 ,1.0 ,2.0 ,3.0 ,4.0 ,5.0 ,6.0 ],
  [14/31 ,1.0   ,2.0 ,3.0 ,4.0 ,5.0 ,6.0 , 10000.0]
])

y = [0.439, 0.256, 0.191, 0.051, 0.015, 0.007, 0.014, 0.027]

parameters, covariances = curve_fit(_get_delta_nbd, x, y)
print('parameters  : ', parameters)
print('covariances : ', covariances)

`}
        </SyntaxHighlighter>


結果として得られた<InlineMath>{`\\displaystyle m `}</InlineMath>と<InlineMath>{`\\displaystyle k `}</InlineMath>は、


<BlockMath>{`
\\begin{aligned}
m&= 1.378 \\\\\\\\
k&= 4.144
\\end{aligned}
`}</BlockMath>


となり、筆者が利用した<InlineMath>{`\\displaystyle m `}</InlineMath>と<InlineMath>{`\\displaystyle k `}</InlineMath>


<BlockMath>{`
\\begin{aligned}
m&= 1.37552 \\\\\\\\
k&= 4.061
\\end{aligned}
`}</BlockMath>

とほぼ等しい値になっています。




      </div>
    )   
  }
}

export default Chapter201;
